import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ChartPieIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  ReceiptPercentIcon,
  StarIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

import { apiErrorMessage } from "../../services/api";
import { fetchDashboardWidget } from "../../services/dashboardService";
import { ChartBody } from "../reports/ReportCharts";
import { DASH, figureDir, fmtLocale, formatKpiValue, formatPercent } from "../reports/reportFormat";
import { formatDate } from "../../services/datetime";

const ICONS = {
  kpi_collections: BanknotesIcon,
  kpi_completed_visits: UserGroupIcon,
  kpi_average_ticket: ReceiptPercentIcon,
  kpi_active_clients: UserGroupIcon,
  kpi_utilization: ChartPieIcon,
  kpi_balances_due: ClipboardDocumentListIcon,
  my_visits: CheckBadgeIcon,
  my_rating: StarIcon,
};

const SEVERITY = {
  critical: "bg-danger",
  high: "bg-danger",
  medium: "bg-warning",
  low: "bg-ink-2",
};

/** Widgets with a Daily / Weekly toggle. */
const GRAINED = ["collections_trend", "booking_outcomes", "new_returning"];

/** A report URL in the Reports page's own query format (lists as "a,b"). */
export const reportPath = (report, extra = {}) => {
  if (!report) return null;

  const params = new URLSearchParams();
  Object.entries({ ...report.query, ...extra }).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    params.set(key, Array.isArray(value) ? value.join(",") : String(value));
  });

  const query = params.toString();

  return `/reports/${report.category}/${report.key}${query ? `?${query}` : ""}`;
};

/**
 * One dashboard widget: loads on its own, with its own skeleton, empty and
 * error states, so a failing widget never blanks the page (spec 07).
 */
/**
 * `reload` — `{ n, refresh }` from the page: a new `n` asks again (back to
 * the tab: quietly, keeping the figures on screen); `refresh` also skips the
 * server cache (the Refresh button).
 */
export default function DashboardWidget({ widgetKey, size, title, filters, onLoaded, reload }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [grain, setGrain] = useState("day");
  const [metric, setMetric] = useState("");
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  const filterKey = JSON.stringify(filters);
  const reloadN = reload?.n ?? 0;
  const refresh = Boolean(reload?.refresh);
  // What was last asked for: a reload of the same question keeps its figures up.
  const asked = useRef("");

  useEffect(() => {
    let cancelled = false;
    const question = JSON.stringify([widgetKey, filterKey, grain, metric, i18n.language]);
    const same = asked.current === question;
    asked.current = question;
    setState((s) => ({ ...s, loading: !(same && s.data), error: "" }));

    fetchDashboardWidget(widgetKey, {
      ...JSON.parse(filterKey),
      ...(GRAINED.includes(widgetKey) ? { grain } : {}),
      ...(metric ? { metric } : {}),
      ...(refresh ? { refresh: 1 } : {}),
    })
      .then((data) => {
        if (cancelled) return;
        setState({ loading: false, data, error: "" });
        onLoaded?.(data.meta);
      })
      .catch((err) => {
        if (cancelled) return;
        onLoaded?.(null);
        setState({
          loading: false,
          data: null,
          error: apiErrorMessage(err, t("dashboard.v2.widgetFailed")),
        });
      });

    return () => {
      cancelled = true;
    };
    // `refresh` rides along with `reloadN`; it never asks again on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetKey, filterKey, grain, metric, i18n.language, t, onLoaded, reloadN]);

  const { loading, data, error } = state;
  const open = (extra = {}) => {
    const path = reportPath(data?.report, extra);
    if (path) navigate(path);
  };

  if (size === "kpi") {
    return <KpiCard widgetKey={widgetKey} title={title} state={state} onOpen={() => open()} />;
  }

  const span = size === "two_thirds" ? "lg:col-span-2" : size === "full" ? "lg:col-span-3" : "";

  const controls = (
    <div className="flex items-center gap-2">
      {GRAINED.includes(widgetKey) && (
        <select
          className="field-input h-8 w-auto py-0 text-dense"
          value={grain}
          onChange={(e) => setGrain(e.target.value)}
          aria-label={t("dashboard.v2.grain")}
        >
          <option value="day">{t("dashboard.v2.daily")}</option>
          <option value="week">{t("dashboard.v2.weekly")}</option>
        </select>
      )}
      {widgetKey === "branch_performance" && data?.metrics?.length > 0 && (
        <select
          className="field-input h-8 w-auto py-0 text-dense"
          value={data.metric}
          onChange={(e) => setMetric(e.target.value)}
          aria-label={t("dashboard.v2.metric")}
        >
          {data.metrics.map((m) => (
            <option key={m} value={m}>
              {t(`dashboard.v2.metrics.${m}`)}
            </option>
          ))}
        </select>
      )}
    </div>
  );

  return (
    <section className={`card min-w-0 p-4 ${span}`} aria-busy={loading}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-card-title font-bold">{title}</h2>
        {controls}
      </div>

      {loading && !data ? (
        <div className="h-48 animate-pulse rounded-lg bg-raised" />
      ) : error ? (
        <p className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-dense text-danger-text">
          {error}
        </p>
      ) : (
        <Body data={data} onOpen={open} t={t} locale={i18n.language} />
      )}
    </section>
  );
}

function Body({ data, onOpen, t, locale }) {
  if (data.type === "chart") {
    const chart = data.chart;
    const currencies = [...new Set((chart?.series ?? []).map((s) => s.currency ?? null))];
    const measures = chart?.measures ?? [];
    const shown = measures.length
      ? {
          ...chart,
          value_type: measures[0].value_type,
          series: chart.series.filter((s) => s.measure === measures[0].key),
        }
      : chart;

    // A money chart in "All currencies" draws one panel per currency.
    const panels = currencies.length > 1 && !measures.length ? currencies : [null];

    return (
      <div className="space-y-4 overflow-x-auto">
        {panels.map((currency) => (
          <div key={currency ?? "all"}>
            {currency && <p className="mb-1 text-caption text-ink-2">{currency}</p>}
            <ChartBody
              chart={
                currency ? { ...shown, series: shown.series.filter((s) => s.currency === currency) } : shown
              }
              onPoint={(point) => onOpen(point ? { drill: point } : {})}
            />
          </div>
        ))}
      </div>
    );
  }

  if (data.type === "attention") {
    if (!data.items.length)
      return <p className="py-8 text-center text-dense text-ink-2">{t("dashboard.v2.allClear")}</p>;

    return (
      <ul className="divide-y divide-line">
        {data.path && (
          <li className="pb-2 text-end">
            <Link to={data.path} className="text-caption font-semibold text-accent-text hover:underline">
              {t("dashboard.v2.allAlerts")}
            </Link>
          </li>
        )}
        {data.items.map((item) => (
          <li key={item.type}>
            <Link
              to={item.path ?? reportPath(item.report)}
              className="flex items-center gap-3 py-2.5 text-dense hover:bg-raised"
            >
              <span
                className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${SEVERITY[item.severity] ?? "bg-ink-2"}`}
                aria-hidden="true"
              />
              <span className="flex-1">{item.label}</span>
              <span className="rounded-md border border-warning-line bg-warning-wash px-2.5 py-0.5 text-caption font-semibold text-warning-text">
                {t(`dashboard.v2.actions.${item.action}`)}
              </span>
              <ChevronRightIcon className="h-4 w-4 text-ink-2 rtl:rotate-180" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    );
  }

  if (data.type === "status_counts") {
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {data.counts.map((c) => (
          <button
            key={c.status}
            type="button"
            onClick={() => onOpen({ status: c.status })}
            className="min-w-0 break-words rounded-lg border border-line p-3 text-start hover:bg-raised"
          >
            <p className="text-xl font-bold tabular-nums">{c.count}</p>
            <p className="text-caption text-ink-2">{c.label}</p>
          </button>
        ))}
      </div>
    );
  }

  if (data.type === "list") {
    if (!data.items.length)
      return <p className="py-8 text-center text-dense text-ink-2">{t("dashboard.v2.nothingScheduled")}</p>;

    let lastDate = null;

    return (
      <ul className="max-h-80 divide-y divide-line overflow-y-auto">
        {data.items.map((item) => {
          const heading = item.date && item.date !== lastDate ? item.date : null;
          lastDate = item.date ?? lastDate;

          return (
            <li key={`${item.id}-${item.time}`}>
              {heading && (
                <p className="bg-raised px-2 py-1 text-caption font-semibold text-ink-2">
                  {formatDate(heading, locale, { weekday: "short" })}
                </p>
              )}
              <Link to={item.path} className="flex items-center gap-3 px-2 py-2 text-dense hover:bg-raised">
                <span className="w-12 font-semibold tabular-nums" dir="ltr">
                  {item.time}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {[item.client, item.services ?? item.service, item.provider].filter(Boolean).join(" · ") ||
                    DASH}
                </span>
                <span className="text-caption text-ink-2">{item.status}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    );
  }

  if (data.type === "count") {
    return (
      <Link to={data.link} className="block rounded-lg p-2 hover:bg-raised">
        <p className="text-3xl font-bold tabular-nums">{data.count}</p>
        <p className="text-dense text-ink-2">{t("dashboard.v2.dueTodayHint")}</p>
      </Link>
    );
  }

  if (data.links) {
    return (
      <div className="flex flex-col gap-2">
        {data.links.map((l) => (
          <Link
            key={l.key}
            to={l.path}
            className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-dense font-semibold hover:bg-raised"
          >
            {t(`dashboard.v2.links.${l.key}`)}
            <ChevronRightIcon className="h-4 w-4 text-ink-2 rtl:rotate-180" aria-hidden="true" />
          </Link>
        ))}
      </div>
    );
  }

  return null;
}

/** A KPI card: icon, value per currency, change and a sparkline. */
function KpiCard({ widgetKey, title, state, onOpen }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { loading, data, error } = state;
  const Icon = ICONS[widgetKey] ?? CalendarDaysIcon;
  const kpi = data?.kpi;
  const values = kpi?.values ?? [];
  const isRate = kpi?.type === "percent";
  const danger = data?.tone === "danger";

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!data?.report}
      className="card flex min-w-0 items-start gap-3 p-4 text-start transition hover:shadow-md disabled:cursor-default"
      aria-busy={loading}
    >
      <span
        className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl ${danger ? "bg-danger-wash text-danger-text" : "bg-accent-wash text-accent-text"}`}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-dense text-ink-2">{kpi?.label ?? title}</span>
        {loading && !data ? (
          <span className="mt-1 block h-7 w-24 animate-pulse rounded bg-raised" />
        ) : error ? (
          <span className="mt-1 block text-caption text-danger-text">{error}</span>
        ) : (
          values.map((v, index) => {
            const shown = v.unknown && !v.current
              ? DASH
              : isRate
                ? formatPercent(v.current, locale, t)
                : formatKpiValue(v.current, kpi.type, locale, t);

            return (
            <span key={v.currency ?? index} className="mt-0.5 block">
              <span className="flex flex-wrap items-baseline gap-x-2">
                <span className={`font-bold ${values.length > 1 ? "text-lg" : "text-xl"}`} dir={figureDir(shown)}>
                  {shown}
                </span>
                {v.count !== null && v.count !== undefined && (
                  <span className="text-caption text-ink-2">
                    {t("reports.shell.countOf", { count: v.count })}
                  </span>
                )}
              </span>
              <span className="flex items-center justify-between gap-2">
                <Change comparison={v.comparison} higherIsBetter={kpi.higher_is_better} />
                <Sparkline values={data.sparkline?.[v.currency ?? ""]} danger={danger} />
              </span>
            </span>
            );
          })
        )}
        {data?.sub && <span className="block text-caption text-ink-2">{data.sub}</span>}
      </span>
    </button>
  );
}

function Change({ comparison, higherIsBetter }) {
  const { t, i18n } = useTranslation();

  if (!comparison) return <span />;

  if (comparison.change_label === "new")
    return <span className="text-caption text-ink-2">{t("reports.change.new")}</span>;
  if (comparison.change_pct === null && comparison.change_pp === null)
    return <span className="text-caption text-ink-2">{t("reports.change.na")}</span>;

  const up = comparison.direction === "up";
  const good = comparison.direction === "flat" ? null : up === higherIsBetter;
  const Arrow = up ? ArrowUpIcon : ArrowDownIcon;
  const number = new Intl.NumberFormat(fmtLocale(i18n.language), { maximumFractionDigits: 1, signDisplay: "always" });
  const isPoints = comparison.change_pp !== null && comparison.change_pp !== undefined;
  const text =
    comparison.change_pp !== null && comparison.change_pp !== undefined
      ? t("dashboard.v2.points", { value: number.format(comparison.change_pp) })
      : `${number.format(comparison.change_pct)}%`;

  return (
    <span
      className={`inline-flex items-center gap-1 text-caption font-semibold ${good === null ? "text-ink-2" : good ? "text-accent-text" : "text-danger-text"}`}
      dir={isPoints ? "auto" : "ltr"}
    >
      {comparison.direction !== "flat" && <Arrow className="h-3 w-3" aria-hidden="true" />}
      {text}
    </span>
  );
}

/** A tiny line of the period's values; gaps (unknown) are skipped. */
function Sparkline({ values, danger }) {
  const points = (values ?? []).map((v, i) => [i, v]).filter(([, v]) => v !== null && v !== undefined);

  if (points.length < 2) return null;

  const max = Math.max(...points.map(([, v]) => v));
  const min = Math.min(...points.map(([, v]) => v));
  const width = 64;
  const height = 20;
  const last = values.length - 1 || 1;
  const path = points
    .map(
      ([i, v]) =>
        `${((i / last) * width).toFixed(1)},${(height - (max === min ? height / 2 : ((v - min) / (max - min)) * height)).toFixed(1)}`
    )
    .join(" ");

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      // Time runs right-to-left in Arabic, like the report charts' x axis.
      className={`${danger ? "text-danger" : "text-accent"} rtl:-scale-x-100`}
      aria-hidden="true"
    >
      <polyline
        points={path}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
